import { useEffect, useRef } from "react";

// Signature scroll "thread": an SVG sine wave drawn down the reading-side
// gutter that fills as the page scrolls, with a glowing dot at the fill tip.
// Auto-hides when the gutter is too narrow (and under 1400px via CSS).
// Honors prefers-reduced-motion (renders nothing).
const CONTENT = 1240;
const MIN_GUTTER = 78;

const GutterThread = () => {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const baseRef = useRef(null);
  const fillRef = useRef(null);
  const dotRef = useRef(null);
  const haloRef = useRef(null);

  useEffect(() => {
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const wrap = wrapRef.current;
    if (!wrap || reduce) {
      return undefined;
    }

    const svg = svgRef.current;
    const base = baseRef.current;
    const fill = fillRef.current;
    const dot = dotRef.current;
    const halo = haloRef.current;

    let len = 0;
    let on = false;
    let progress = 0;

    const build = () => {
      const w = document.documentElement.clientWidth;
      // Height of the Home region the thread lives in (its positioned parent).
      const h = wrap.offsetHeight || document.documentElement.scrollHeight;
      const gutter = (w - CONTENT) / 2;
      on = gutter >= MIN_GUTTER && h > 0;
      wrap.style.display = on ? "block" : "none";
      if (!on) {
        return;
      }
      // RTL reading side = right gutter.
      const cx = w - gutter / 2;
      const amp = Math.min(26, gutter / 3.4);
      svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
      const steps = 90;
      let d = "";
      for (let i = 0; i <= steps; i++) {
        const tt = i / steps;
        const y = tt * h;
        const x = cx + Math.sin(tt * Math.PI * 5.5) * amp;
        d += (i === 0 ? "M " : " L ") + x.toFixed(1) + " " + y.toFixed(1);
      }
      base.setAttribute("d", d);
      fill.setAttribute("d", d);
      len = fill.getTotalLength();
      fill.style.strokeDasharray = len;
      fill.style.strokeDashoffset = len;
    };

    const update = (p) => {
      progress = p;
      if (!on || !len) {
        return;
      }
      fill.style.strokeDashoffset = len * (1 - p);
      const pt = fill.getPointAtLength(len * p);
      dot.style.transform = `translate(${pt.x}px, ${pt.y}px)`;
      halo.style.transform = `translate(${pt.x}px, ${pt.y}px)`;
      const vis = p > 0.004 ? "1" : "0";
      dot.style.opacity = vis;
      halo.style.opacity = vis;
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        const max = document.documentElement.scrollHeight - window.innerHeight;
        update(max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0);
      });
    };

    const rebuild = () => {
      build();
      update(progress);
    };

    build();
    rebuild();
    const t = window.setTimeout(rebuild, 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", rebuild);
    const ro = new ResizeObserver(rebuild);
    ro.observe(document.body);
    onScroll();

    return () => {
      window.clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", rebuild);
      ro.disconnect();
    };
  }, []);

  return (
    <div ref={wrapRef} className="gutter-thread" aria-hidden="true">
      <svg ref={svgRef} preserveAspectRatio="none" className="gutter-thread__svg">
        <path ref={baseRef} d="" fill="none" stroke="rgba(120,150,220,.18)" strokeWidth="1.5" />
        <path ref={fillRef} d="" fill="none" stroke="#3b7df5" strokeWidth="1.5" />
      </svg>
      <div ref={dotRef} className="gutter-thread__dot" />
      <div ref={haloRef} className="gutter-thread__halo" />
    </div>
  );
};

export default GutterThread;
